/**
 * Cross-reload signalling for `notify()` calls that fire immediately
 * before a `window.location.reload()` (or a navigation that throws away
 * the React tree).
 *
 * Why this exists:
 *
 *   The snackbar host is rendered inside `UIProvider`, which is part of
 *   the React tree. A hard reload tears the tree down and kills every
 *   queued snackbar with it. Several "do thing, show toast, reload"
 *   flows (creating an enterprise, switching enterprise, the Vitalora
 *   demo reset, etc.) end up showing the toast for ~1 second before
 *   the reload wipes it — which the user perceives as no toast at all.
 *
 *   `frontends/carrie/src/utils/shareLinkInviteToast.ts` already solved
 *   this for ONE specific case (the share-link "we just sent you an
 *   email" toast). This module generalises that pattern so any caller
 *   can stash an arbitrary `notify({title, message, variant, ...})`
 *   payload across a reload.
 *
 * Invariants every caller must honour:
 *
 *   1. Read-and-clear. `consumePendingNotifications` ALWAYS clears the
 *      slot on read so a stale notification can't leak into a future
 *      unrelated session. (Treat the storage as a single-shot mailbox.)
 *
 *   2. Multiple stashes accumulate. Calling `stashPendingNotification`
 *      twice before a reload queues TWO notifications — the consumer
 *      replays both in stash order. This matches user expectation for
 *      e.g. bulk-import flows that fire multiple toasts in a row.
 *
 *   3. SSR + locked-down browser safety. Every storage op is wrapped
 *      in try/catch and silently no-ops if `sessionStorage` is
 *      unavailable (Safari private mode, Chrome with storage disabled,
 *      SSR pre-hydration). A missing toast is strictly better than a
 *      thrown error mid-reload.
 *
 *   4. Defensive deserialization. If the stored JSON is malformed
 *      (older app version wrote a different schema, hand-edited
 *      sessionStorage, etc.) we clear the slot and return an empty
 *      array instead of throwing. Same reasoning as (3) — never block
 *      app startup on a toast.
 */

import type { SnackbarProps } from "@mg/dali/src";

/**
 * Storage key used to hold the JSON-serialised pending-notification
 * queue. Exported so unit tests can clear it between cases and so
 * future callers can spot the contract without re-grepping.
 */
export const PENDING_NOTIFICATIONS_KEY = "puntt.pendingNotifications";

/**
 * Subset of `SnackbarProps` that's safely JSON-serialisable across a
 * page reload. Excludes:
 *
 *   - React node fields (`leadingIcon`, `actionButtons`) — these
 *     wouldn't survive `JSON.stringify` anyway. The consumer
 *     re-applies sensible defaults based on `variant`.
 *   - DOM event handlers (`onOpen`, `onClose`) — same reason.
 *   - HTMLAttribute spread props — niche; not worth the
 *     serialisation risk for a cross-reload toast.
 */
export interface PendingNotificationPayload {
  title?: string;
  message?: string;
  variant?: SnackbarProps["variant"];
  timeout?: number;
  autoDismiss?: boolean;
}

/**
 * Queue a notification to fire after the next page reload / remount of
 * `UIProvider`. The caller typically:
 *
 *   stashPendingNotification({ title: "Enterprise Created", ... });
 *   // ... mutation, navigation, then:
 *   window.location.reload();
 *
 * The next time `UIProvider` mounts it drains this queue via
 * `consumePendingNotifications` and emits each entry through `notify()`.
 *
 * No-ops silently when sessionStorage is unavailable (locked-down
 * browser / SSR). The caller doesn't have to branch — a missing toast
 * is the failure mode by design.
 */
export function stashPendingNotification(
  payload: PendingNotificationPayload,
): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_NOTIFICATIONS_KEY);
  } catch {
    return;
  }

  let queue: PendingNotificationPayload[] = [];
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        // We trust the existing on-disk queue here. The reader
        // (`consumePendingNotifications`) is responsible for filtering
        // out malformed entries before emitting them — that's the
        // single source of truth for what counts as a valid payload.
        queue = parsed as PendingNotificationPayload[];
      }
    } catch {
      // Malformed JSON — start fresh. The next `consumePendingNotifications`
      // call would have cleared it anyway.
    }
  }

  queue.push(payload);

  try {
    sessionStorage.setItem(PENDING_NOTIFICATIONS_KEY, JSON.stringify(queue));
  } catch {
    // ignore — see header rationale (3)
  }
}

/**
 * Read-and-clear every queued notification. Returns them in stash
 * order so the consumer can `notify()` each one in turn.
 *
 * ALWAYS clears the slot on read (even when parsing fails or the slot
 * is empty) so a stale value can't leak into a future session. Returns
 * an empty array on any error.
 *
 * Idempotent across remounts: a second consume call returns `[]` until
 * something new is stashed.
 */
export function consumePendingNotifications(): PendingNotificationPayload[] {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_NOTIFICATIONS_KEY);
  } catch {
    return [];
  }

  // Always clear, even on the empty-or-malformed paths. We want this
  // function to be a true single-shot mailbox read.
  try {
    sessionStorage.removeItem(PENDING_NOTIFICATIONS_KEY);
  } catch {
    // ignore
  }

  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Filter defensively in case a future version writes a different
    // shape: only return entries with at least one displayable field.
    // `entry` is `unknown` here (the array's element type after
    // JSON.parse), so we narrow via `Record<string, unknown>` before
    // probing `.title` / `.message`.
    return parsed.filter(
      (entry: unknown): entry is PendingNotificationPayload => {
        if (!entry || typeof entry !== "object") return false;
        const obj = entry as Record<string, unknown>;
        return typeof obj.title === "string" || typeof obj.message === "string";
      },
    );
  } catch {
    return [];
  }
}
